import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useState } from 'react';
import { Collapse, Alert as MuiAlert, alertClasses as MuiAlertClasses } from '@mui/material';
import CheckCircleOutline from 'mdi-material-ui/CheckCircleOutline';
import CloseIcon from 'mdi-material-ui/Close';
import MenuDown from 'mdi-material-ui/MenuDown';
import MenuUp from 'mdi-material-ui/MenuUp';
import CheckDecagramOutline from 'mdi-material-ui/CheckDecagramOutline';
import { combineSx } from '../../utils';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { Button } from '../Button';
import { Typography } from '../Typography';
export const alertClasses = {
    message: MuiAlertClasses.message,
    action: MuiAlertClasses.action
};
/*

Internal overrides for some icons that we are customizing outside of MUI (or for severity variants that don't exist there):

*/ const getIcon = (severity)=>{
    if (severity === 'success') return /*#__PURE__*/ _jsx(CheckCircleOutline, {});
    if (severity === 'new') return /*#__PURE__*/ _jsx(CheckDecagramOutline, {});
};
/*

There is a bug in versions of MUI below 5.15.4 where the `severity` prop can't be overridden like `color` can be
and `AlertColor` is restricted to the 4 default options (info, warning, error, success).
https://github.com/mui/material-ui/pull/40551

Until we can upgrade, we need to extend the options outside of the component and proxy in a valid option to keep
the linter happy.

*/ const getMuiSeverity = (severity)=>{
    if (severity === 'new') return 'info';
    return severity;
};
export const Alert = /*#__PURE__*/ forwardRef(function Alert({ severity = 'success', hideIcon, square, Action, children, onClose, MoreDetails, Header, sx, ...props }, ref) {
    const [detailsVisible, setDetailsVisible] = useState(false);
    const icon = getIcon(severity);
    const ariaLabel = props['aria-label'];
    return /*#__PURE__*/ _jsxs(MuiAlert, {
        sx: combineSx({
            [`&.${MuiAlertClasses.root}`]: {
                alignItems: 'flex-start'
            },
            ...severity === 'new' && {
                [`&.${MuiAlertClasses.standardInfo}`]: {
                    color: (t)=>t.palette.status.new.highContrast,
                    backgroundColor: (t)=>t.palette.status.new.lowContrast,
                    [`.${MuiAlertClasses.icon}`]: {
                        color: (t)=>t.palette.status.new.highContrast
                    }
                }
            }
        }, sx),
        severity: getMuiSeverity(severity),
        square: square,
        ...props,
        ref: ref,
        icon: hideIcon ? false : icon,
        "aria-label": ariaLabel,
        action: Action || onClose ? // The 2px vertical margin is necessary to make the action buttons look centered when the
        // alert doesn't grow vertically
        /*#__PURE__*/ _jsxs(Stack, {
            direction: "row",
            gap: 1,
            my: "2px",
            children: [
                Action,
                onClose && /*#__PURE__*/ _jsx(IconButton, {
                    "aria-label": ariaLabel ? `close ${ariaLabel}` : `close alert`,
                    onClick: onClose,
                    children: /*#__PURE__*/ _jsx(CloseIcon, {})
                })
            ]
        }) : undefined,
        children: [
            Header && /*#__PURE__*/ _jsx(Typography, {
                fontWeight: "bold",
                variant: "h4",
                children: Header
            }),
            children,
            MoreDetails && /*#__PURE__*/ _jsxs(Stack, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>setDetailsVisible(!detailsVisible),
                        StartIcon: detailsVisible ? /*#__PURE__*/ _jsx(MenuUp, {}) : /*#__PURE__*/ _jsx(MenuDown, {}),
                        variant: "tertiary",
                        size: "small",
                        sx: {
                            width: 'fit-content',
                            padding: 0,
                            fontSize: (theme)=>theme.typography.body2,
                            fontWeight: 'bold'
                        },
                        children: "More details"
                    }),
                    /*#__PURE__*/ _jsx(Collapse, {
                        in: detailsVisible,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            sx: {
                                marginLeft: '28px'
                            },
                            variant: "body2",
                            children: MoreDetails
                        })
                    })
                ]
            })
        ]
    });
});

//# sourceMappingURL=Alert.js.map